import { useState } from "react";
import { useHistory } from "react-router-dom";
import { ICustomerDetails } from "@interface/customerDetails/ICustomerDetails";
import { getStateToPayload as getCustomerDetailsStateToPayload } from "@features/customerDetails/mappers/updateCustomerDetailsMapper";
import { ICustomerDetailsResponse } from "@modules/eirSalesFacade/interfaces/responses/IEirSalesCustomerDetailsResponse";
import { getStateToPayload as getTermsAndConditionsStateToPayload } from "@features/customerDetails/mappers/updateTermsAmdConditionsMapper";
import { getStateToPayload as getSendContractStateToPayload } from "@features/customerDetails/mappers/sendContractMapper";
import { useFetch } from "./useFetch";
import { IError, keycloakUtils } from "itsf-ui-common";
import { postCustomerDetailsPhoneNumberRoute, putCustomerDetailsRoute, sendContract } from "@api-routes/eirSalesFacade";
import { updateTermsAndConditionRoute } from "@api-routes/acquisitionCrossSell";
import { putContactAddressApiRoute } from "@api-routes/contacts";
import { shouldCallPutDeliveryAddress } from "@features/customerDetails/utils/customerDetailsUtils";
import { ICart } from "@features/cart/ICart";
import { mapUpdateDeliveryAddressPayload } from "@features/customerDetails/mappers/mapUpdateDeliveryAddressPayload";
import { IStepRoute } from "@common/StepperRouter";
import { getProspectReference } from "@utils/storage";
import { mapEirSalesSaveCustomerPhoneNumberPayload } from "@modules/eirSalesFacade/mappers/mapEirSalesSaveCustomerPhoneNumberPayload";
import { EMAIL_ALREADY_EXISTS_ERROR_CODES } from "@constants";

export const useUpdateCustomerDetails = (nextStep?: IStepRoute) => {
    const { push } = useHistory();
    const [updateCustomerDetailsState, { put, patch, post }] = useFetch();
    const [updateCustomerDeliveryAddressState, { put: putAddress }] = useFetch();
    const [updateCustomerPhoneNumberState, { post: postPhoneNumber }] = useFetch();
    const [updateCustomerDetailsError, setUpdateCustomerDetailsError] = useState<IError>();

    const updateCustomerDetailsAcquisition = (customerDetails: ICustomerDetails, recaptchaValue: string | undefined) =>
        put<ICustomerDetailsResponse>(putCustomerDetailsRoute(), {
            body: getCustomerDetailsStateToPayload(customerDetails, recaptchaValue),
            forwardError: true,
        });

    const updateCustomerDeliveryAddress = (customerDetails: ICustomerDetails, contactUuid: string) =>
        putAddress<void>(putContactAddressApiRoute(contactUuid, "DELIVERY"), {
            body: mapUpdateDeliveryAddressPayload(customerDetails),
        });

    const updateCustomerPhoneNumber = (contactUuid: string, mobileNumber: string, reference: string) =>
        postPhoneNumber<void>(postCustomerDetailsPhoneNumberRoute(contactUuid), {
            body: mapEirSalesSaveCustomerPhoneNumberPayload(mobileNumber, reference),
        });

    const updateCustomerDetails = async (
        customerDetails: ICustomerDetails,
        cart: ICart | undefined,
        privacyPolicyValue: boolean,
        recaptchaValue: string | undefined,
        setEmailError: (errorMessage?: string) => void
    ) => {
        setEmailError();
        setUpdateCustomerDetailsError(undefined);

        try {
            const isAuthenticated = keycloakUtils.isAuthenticated();
            const userProfile = await keycloakUtils.getUserProfile();
            const contactUuid = userProfile?.username;
            const prospectReference = getProspectReference();

            if (!isAuthenticated) {
                await updateCustomerDetailsAcquisition(customerDetails, recaptchaValue);
            } else if (contactUuid) {
                if (customerDetails.mobileNumber) {
                    await updateCustomerPhoneNumber(contactUuid, customerDetails.mobileNumber, prospectReference);
                }

                if (shouldCallPutDeliveryAddress(cart)) {
                    await updateCustomerDeliveryAddress(customerDetails, contactUuid);
                }
            }

            const result = await patch<boolean>(updateTermsAndConditionRoute(), {
                body: getTermsAndConditionsStateToPayload(privacyPolicyValue),
            });

            await post(sendContract(), {
                body: getSendContractStateToPayload(prospectReference, isAuthenticated),
            });

            if (result && nextStep) {
                push({ pathname: nextStep.path });
            }
        } catch (err: unknown) {
            const error = err as IError;
            if (EMAIL_ALREADY_EXISTS_ERROR_CODES.includes(error.errorCode ?? "")) {
                setEmailError(error.errorMessage);
            } else {
                setUpdateCustomerDetailsError(error);
            }
        }
    };

    return {
        updateCustomerDetailsState,
        updateCustomerDeliveryAddressState,
        updateCustomerPhoneNumberState,
        updateCustomerDetailsError,
        updateCustomerDetails,
    };
};
